/**
 * StatusBadge - Visual indicator for order lifecycle status
 * 
 * Displays the current status of an order with appropriate styling.
 * Used in order lists, map view info windows, and order details.
 * 
 * Statuses:
 * - new: Orange/amber - Order in quarantine, needs acknowledgment
 * - accepted: Blue - Order acknowledged, in production queue
 * - designed: Purple - Arrangement complete, ready for delivery assignment
 * - on_route: Green - Assigned to committed delivery run
 * - delivered: Gray/muted - Delivery confirmed (terminal state)
 */
import React from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  Palette, 
  Truck, 
  PackageCheck,
  XCircle
} from 'lucide-react';

// Status configuration with colors, icons, and labels
const STATUS_CONFIG = {
  new: {
    label: 'New',
    description: 'Awaiting acknowledgment',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-300',
    dotColor: 'bg-amber-500',
    Icon: AlertCircle,
  },
  accepted: {
    label: 'Accepted',
    description: 'In production queue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-300',
    dotColor: 'bg-blue-500',
    Icon: CheckCircle,
  },
  designed: {
    label: 'Designed',
    description: 'Ready for delivery',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    borderColor: 'border-purple-300',
    dotColor: 'bg-purple-500',
    Icon: Palette,
  },
  on_route: {
    label: 'On Route',
    description: 'Out for delivery',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    dotColor: 'bg-green-500',
    Icon: Truck,
  },
  delivered: {
    label: 'Delivered',
    description: 'Delivery complete',
    bgColor: 'bg-stone-100',
    textColor: 'text-stone-600',
    borderColor: 'border-stone-300',
    dotColor: 'bg-stone-400',
    Icon: PackageCheck,
  },
  failed: {
    label: 'Failed',
    description: 'Delivery failed',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    borderColor: 'border-red-300',
    dotColor: 'bg-red-500',
    Icon: XCircle,
  },
};

// Default fallback for unknown statuses
const DEFAULT_CONFIG = {
  label: 'Unknown',
  description: 'Status unknown',
  bgColor: 'bg-gray-100',
  textColor: 'text-gray-600',
  borderColor: 'border-gray-300',
  dotColor: 'bg-gray-400',
  Icon: AlertCircle,
};

/**
 * StatusBadge Component
 * 
 * @param {string} status - Order status (new, accepted, designed, on_route, delivered, failed)
 * @param {string} size - Badge size: 'sm' (compact), 'md' (default), 'lg' (with icon)
 * @param {boolean} showIcon - Whether to show the status icon
 * @param {boolean} showDot - Whether to show a colored dot instead of full background
 * @param {string} className - Additional CSS classes
 */
const StatusBadge = ({ 
  status, 
  size = 'md', 
  showIcon = false, 
  showDot = false,
  className = '' 
}) => {
  const config = STATUS_CONFIG[status] || DEFAULT_CONFIG;
  const { label, bgColor, textColor, borderColor, dotColor, Icon } = config;

  // Dot variant - minimal, just a colored dot and text
  if (showDot) {
    return (
      <span 
        className={`inline-flex items-center gap-1.5 ${className}`}
        data-testid={`status-badge-${status}`}
      >
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className={`text-xs font-medium ${textColor}`}>{label}</span>
      </span>
    );
  }

  // Size-based classes
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium border
        ${bgColor} ${textColor} ${borderColor} ${sizeClasses[size]} ${className}
      `}
      data-testid={`status-badge-${status}`}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {label}
    </span>
  );
};

/**
 * Get status configuration for external use (e.g., legends, filters)
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || DEFAULT_CONFIG;
};

/**
 * Get all available statuses
 */
export const getAllStatuses = () => {
  return Object.keys(STATUS_CONFIG);
};

export default StatusBadge;
